<script>
import form from '@/utils/form';
import BaseField from '@/components/BaseField.vue';

/**
 * Универсальная форма: рисует поля по конфигу и отдаёт наружу готовые значения.
 * Конфиг — объект вида { имяПоля: form.createFieldObject(...) }, обычно из
 * src/config/dir/*, но подойдёт любой.
 *
 * Форма не мутирует переданный конфиг: поля копируются в локальное состояние,
 * поэтому один и тот же конфиг можно открывать сколько угодно раз.
 */
export default {
  name: 'BaseForm',
  components: { BaseField },
  props: {
    fields: { type: Object, default: () => ({}) },
    values: { type: Object, default: () => ({}) },
    title: String,
    submitText: { type: String, default: 'Сохранить' },
    cancelText: { type: String, default: 'Отмена' },
  },
  data() {
    return {
      localFields: {},
    };
  },
  created() {
    this.init();
  },
  watch: {
    fields() { this.init(); },
    values() { this.init(); },
  },
  methods: {
    init() {
      const localFields = Object.fromEntries(
        Object.entries(this.fields).map(([name, field]) => [name, { ...field }]),
      );

      form.setValues(localFields, this.values);
      this.localFields = localFields;
    },
    validateField(name) {
      if (!this.localFields[name].error) return;
      form.validateField(this.localFields, name);
    },
    submit() {
      form.validate(this.localFields);
      if (form.hasErrors(this.localFields)) return;

      this.$emit('submit', form.getValues(this.localFields));
    },
    cancel() {
      this.$emit('cancel');
    },
  },
};
</script>

<template lang="pug">
  form.b-base-form(@submit.prevent="submit")
    ._title(v-if="title") {{ title }}
    ._fields
      BaseField._field(
        v-for="(field, name) in localFields"
        :key="name"
        v-model="field.value"
        v-bind="field"
        labelOuter
        @input="validateField(name)"
      )
    ._buttons
      Btn(grey @click="cancel") {{ cancelText }}
      Btn(green submit) {{ submitText }}
</template>

<style scoped lang="scss">
.base-form {
  display: flex;
  flex-direction: column;
  gap: 30px;
  // Ширину задаёт контейнер (у попапа — BaseFormPopupWrappable), форма её
  // занимает целиком. Не min-width: с ним форма не может сжаться и её поля
  // вылезают за границы попапа на узком экране
  width: 100%;
  box-sizing: border-box;

  &__title {
    font-size: 36px;
    font-weight: 700;
    line-height: 24px;
    letter-spacing: 0.02em;
    word-break: break-word;
  }

  &__fields {
    display: grid;
    gap: 15px;
    min-width: 0;
  }

  &__buttons {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 20px;
  }

  @media (max-width: 576px) {
    gap: 20px;

    &__title {
      font-size: 24px;
      line-height: 1.2;
    }

    &__buttons > * {
      flex: 1 1 auto;
    }
  }
}
</style>
